import endpoint from '@app/endpoint';
import ImageNotAvailableSvg from '@images/icons/image_not_available.svg';
import { startLoading, endLoading } from '@app/actions/spinnerActions';
import {
  STATE_EDITOR,
  RESET_CUSTOMIZE_DATA,
  PERFORM_ACTION,
  CLEANUP_ACTION,
  SET_SNAP2EDGE_ENABLED,
  SET_SNAP2EDGE_VISIBLE,
  SET_EDITOR_ZOOM,
  CHANGE_MEASUREMENT,
  SET_BACKGROUND_PARAMS,
  SET_RESIZE_PARAMS,
  SET_SCALE_PARAMS,
  SET_PANEL_SIZES,
  UPDATE_CUSTOMIZE_ACTION,
  UPDATE_CUSTOMIZE_ACTIONS,
  SET_TOOL_PANEL_OVERLAY_LAYOUT,
  SET_IMAGE_NOT_GENERATED,
  ADD_WORKING_AREA_WARNING,
  REMOVE_WORKING_AREA_WARNING,
  CLEAR_WORKING_AREA_WARNINGS,
  ADD_WORKING_AREA_MESSAGE,
  REMOVE_WORKING_AREA_MESSAGE,
  CLEAR_WORKING_AREA_MESSAGES,
} from './_types';

export const stateEditor = editor => ({
  type: STATE_EDITOR,
  payload: editor,
});

export const resetCustomizeData = () => ({
  type: RESET_CUSTOMIZE_DATA,
});

export const setSnapToEdgeVisible = isVisible => ({
  type: SET_SNAP2EDGE_VISIBLE,
  payload: isVisible,
});

export const setSnapToEdgeEnabled = isEnable => ({
  type: SET_SNAP2EDGE_ENABLED,
  payload: isEnable,
});

export const setEditorZoom = zoomValue => ({
  type: SET_EDITOR_ZOOM,
  payload: zoomValue,
});

export const setToolPanelOverlayLayout = overlayLayout => ({
  type: SET_TOOL_PANEL_OVERLAY_LAYOUT,
  payload: overlayLayout,
});

export const updateCustomizeActions = actions => ({
  type: UPDATE_CUSTOMIZE_ACTIONS,
  payload: actions,
});

export const updateCustomizeAction = (actionName, actionParams = {}) => ({
  type: UPDATE_CUSTOMIZE_ACTION,
  payload: { name: actionName, params: actionParams },
});

export const performAction = (actionName, actionParams = {}) => ({
  type: PERFORM_ACTION,
  payload: { name: actionName, params: actionParams },
});

export const cleanupActions = () => ({
  type: CLEANUP_ACTION,
  payload: {},
});

export const changeMeasurement = value => ({
  type: CHANGE_MEASUREMENT,
  payload: value,
});

export const setResizeParams = params => ({
  type: SET_RESIZE_PARAMS,
  payload: params,
});

export const setScaleParams = params => ({
  type: SET_SCALE_PARAMS,
  payload: params,
});

export const setBgParams = params => ({
  type: SET_BACKGROUND_PARAMS,
  payload: params,
});

export const setPanelSizes = sizes => ({
  type: SET_PANEL_SIZES,
  payload: sizes,
});

export const setImageNotGenerated = value => ({
  type: SET_IMAGE_NOT_GENERATED,
  payload: value,
});

export const addWorkingAreaWarning = warning => (dispatch, getState) => {
  const {
    customize: {
      customizeData: { workingAreaWarnings },
    },
  } = getState();

  const warningAlreadyExist = workingAreaWarnings.some(({ key }) => key === warning.key);

  !warningAlreadyExist &&
    dispatch({
      type: ADD_WORKING_AREA_WARNING,
      payload: warning,
    });
};

export const removeWorkingAreaWarning = warningKey => ({
  type: REMOVE_WORKING_AREA_WARNING,
  payload: warningKey,
});

export const clearWorkingAreaWarnings = () => ({
  type: CLEAR_WORKING_AREA_WARNINGS,
});


export const addWorkingAreaMessage = message => (dispatch, getState) => {
  const {
    customize: {
      customizeData: { workingAreaMessages },
    },
  } = getState();

  const messageAlreadyExist = workingAreaMessages.some(({ key }) => key === message.key);

  !messageAlreadyExist &&
    dispatch({
      type: ADD_WORKING_AREA_MESSAGE,
      payload: message,
    });
};

export const removeWorkingAreaMessage = messageKey => ({
  type: REMOVE_WORKING_AREA_MESSAGE,
  payload: messageKey,
});

export const clearWorkingAreaMessages = () => ({
  type: CLEAR_WORKING_AREA_MESSAGES,
});

export const requestLowResImage = (imageName, projectData) => dispatch =>
  new Promise((resolve, reject) => {
    endpoint.pollingLowResImage(
      imageName,
      false,
      projectData,
      ({ data: { lowResolution } }) => {
        resolve(lowResolution);
      },
      () => {
        dispatch(endLoading());
        reject(ImageNotAvailableSvg);
      },
      this,
    );
  });

export const getImageByUrl = url => dispatch =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (!/^data:image/.test(url)) {
      img.crossOrigin = 'Anonymous';
    }
    img.onload = () => {
      resolve(img);
    };
    img.onerror = res => {
      dispatch(endLoading());
      reject(res);
    };
    img.src = url;
  });

export const fetchShapes = params => dispatch =>
  new Promise((resolve, reject) => {
    const onLoad = ({ data }) => {
      dispatch(endLoading());
      resolve(data);
    }
    const onError = () => {
      dispatch(endLoading());
      reject();
    }

    dispatch(startLoading());
    endpoint.fetchShapes(params, onLoad, onError);
  });

export const applyCustomSizedShape = data => dispatch => {
  return new Promise((resolve, reject) => {
    const {
      projectData,
      project,
      height,
      width,
      productId,
      quoteAssetId,
      cornerRadius,
      units,
    } = data;

    const params = {
      height,
      width,
      units,
      productId,
      cornerRadius,
    };

    const onLoad = ({ data }) => {
      dispatch(endLoading());
      resolve(data);
    }

    const onError = () => {
      dispatch(endLoading());
      reject();
    }

    dispatch(startLoading());
    endpoint.applyCustomSizedPredesignArtwork(params, projectData, project, quoteAssetId, onLoad, onError);
  })
}

export const resetCustomSizedArtwork = data => dispatch => {
  return new Promise((resolve, reject) => {
    const {
      projectData,
      project,
      height,
      width,
      productId,
      units,
    } = data;

    const onLoad = ({ data }) => {
      dispatch(endLoading());
      resolve(data);
    }

    const onError = () => {
      dispatch(endLoading());
      reject();
    }

    dispatch(startLoading());
    endpoint.resetCustomSizedArtwork({
      height,
      width,
      units,
      productId,
    }, projectData, project, onLoad, onError);
  })
}

export const requestImgBgRemoved = data => dispatch => {
  return new Promise((resolve, reject) => {
    const {
      projectData,
      image: { source },
    } = data;

    const onLoad = ({ data }) => {
      dispatch(endLoading());
      resolve(data)
    }

    const onError = () => {
      dispatch(endLoading());
      reject();
    }

    dispatch(startLoading());
    endpoint.requestRmbgImage(source, projectData, onLoad, onError);
  })
};

export const processJob = data => dispatch => {
  return new Promise((resolve, reject) => {
    const {
      projectData,
      jobId,
    } = data;

    const onLoad = ({ data }) => {
      dispatch(endLoading());
      resolve(data)
    }

    const onError = () => {
      dispatch(endLoading());
      reject();
    }

    dispatch(startLoading());
    endpoint.processJobPolling(jobId, projectData, onLoad, onError);
  })
};
